import { BiBell } from "react-icons/bi";
import useNotifications from "../../hooks/useNotification";
import { useEffect, useState, useRef } from "react";
import {
  UserNotification,
  OpenedNotificationParam,
  eNotificationRead,
} from "../../@types/Users";
import { NotificationBody } from "./NotificationBody";
import { LoadingNotificationBody } from "./LoadingNotificationBody";
import { isMobile } from "react-device-detect";
import { EmptyNotifications } from "./EmptyStateBody";
import { ViewOne } from "./ViewOne";
import { useLocation } from "react-router-dom";

/**
 * Locations that doens't need notifications
 */
const notRequestableUrls = [
  "/login",
  "/signup",
  "/password-recovery",
  "/reset-password",
  "/billing",
];

export default function HeaderNotifications() {
  const { getAll } = useNotifications();
  const [notifications, setNotifications] = useState([] as UserNotification[]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expandAll, setExpandAll] = useState<boolean>(false);

  const isAllRead = notifications.every((notification) => notification.is_read);
  const [openedNotificationParams, setOpenedNotificationParams] = useState({
    isOpen: false,
    notification: {} as UserNotification,
  } as OpenedNotificationParam);
  const location = useLocation();
  const requestableRef = useRef({
    isRequestable: false,
  });

  async function loadNotifications() {
    setIsLoading(true);
    const qntToRetrieve = 30;
    const response = await getAll(qntToRetrieve);

    setNotifications(response);

    setIsLoading(false);
  }

  // Load from api on state update
  useEffect(() => {
    loadNotifications();

    // Auto reload
    const threeMinutesInMs = 180000;
    setInterval(() => {
      if (requestableRef.current.isRequestable) {
        loadNotifications();
      }
    }, threeMinutesInMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine if is a requestable location
  useEffect(() => {
    const pathName = location.pathname;
    const newRequestableValue = notRequestableUrls.every(
      (url) => url !== pathName
    );
    requestableRef.current.isRequestable = newRequestableValue;

    if (pathName === "/welcome") {
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  function getSubtitle() {
    if (notifications?.length > 0) {
      const totalUnreadItems = notifications.reduce((acc, item) => {
        if (item.is_read === eNotificationRead.NOT_READ) {
          acc += 1;
        }

        return acc;
      }, 0);

      if (totalUnreadItems > 1) {
        return `Você tem ${totalUnreadItems} notificações não lidas`;
      } else if (totalUnreadItems === 1) {
        return `Você tem 1 notificação não lida`;
      }
    }

    return "Você leu todas notificações";
  }

  return (
    <div className="dropdown az-header-notification">
      <a
        href="#/"
        className={!isAllRead ? "new" : ""}
        onClick={() => {
          setExpandAll(false);
        }}
      >
        <i>
          <BiBell />
        </i>
      </a>

      {openedNotificationParams.isOpen ? (
        // One notification opened
        <ViewOne
          notification={openedNotificationParams.notification}
          setParentNotificationState={setNotifications}
          handleCloseDropdown={() => {
            setOpenedNotificationParams({
              isOpen: false,
              notification: {} as UserNotification,
            });
          }}
        />
      ) : (
        //All notifications listing
        <div
          className="dropdown-menu max-height-dropdown"
          style={!isMobile ? { overflowY: "auto" } : {}}
        >
          <div className="az-dropdown-header mg-b-20 d-sm-none">
            <a href="#/" className="az-header-arrow force-close-dropdown">
              <i className="icon ion-md-arrow-back" />
            </a>
          </div>

          <h6 className="az-notification-title">Notificações</h6>
          {notifications.length > 0 && (
            <p className="az-notification-text">{getSubtitle()}</p>
          )}

          <div className="az-notification-list">
            {
              // Loading skeletons
              isLoading &&
                [0, 1, 2, 3, 4].map((item) => {
                  return <LoadingNotificationBody key={item} />;
                })
            }
            {
              // Empty State Notifications
              !isLoading && notifications.length === 0 && <EmptyNotifications />
            }
            {
              // All available notifications
              !isLoading &&
                notifications.length > 0 &&
                notifications.slice(0, expandAll ? 30 : 5).map((item, i) => {
                  return (
                    <NotificationBody
                      key={i}
                      notification={item}
                      handleOneClick={() => {
                        setOpenedNotificationParams({
                          isOpen: true,
                          notification: item,
                        });
                      }}
                    />
                  );
                })
            }
          </div>
          {
            // Expand all button
            !isLoading && !expandAll && notifications.length > 5 && (
              <div
                className="dropdown-footer"
                onClick={(e) => e.stopPropagation()}
              >
                <a href="#/" onClick={() => setExpandAll(true)}>
                  Ver todas notificações recentes
                </a>
              </div>
            )
          }
        </div>
      )}
    </div>
  );
}
